import { type RuleRecord, type RuleStep, type RuleTrigger } from '@/types/rules.types';
import { isDecision } from './rule-tree';

/**
 * Plain-language hints about how a brand's playbooks interact, read from their steps alone. Runs in
 * the same order as the backend (`store.runnable`): position in the list, then age.
 * The first live playbook that decides wins, so anything below one that always decides never runs;
 * and only one reply goes out per message, so a reply below a live one that replied can be skipped.
 */

const REPLIES = new Set(['reply', 'comment_reply']);

type PathState = 'clean' | 'replied';

/**
 * The states a walk can end in without deciding, in one pass over the steps: a decision ends a path,
 * a reply marks it, a check splits it (an empty check only ever takes its first branch).
 */
function openPaths(steps: RuleStep[], start: ReadonlySet<PathState>): Set<PathState> {
	let states = new Set(start);
	for (const step of steps) {
		if (states.size === 0) break;
		if (step.kind === 'outcome' || isDecision(step)) return new Set();
		if (step.kind === 'action') {
			if (REPLIES.has(step.action)) states = new Set(['replied']);
			continue;
		}
		const viaThen = openPaths(step.then, states);
		states = step.conditions.length === 0 ? viaThen : new Set([...viaThen, ...openPaths(step.else, states)]);
	}
	return states;
}

const CLEAN: ReadonlySet<PathState> = new Set(['clean']);

/** Every path through these steps reaches a decision. */
export const alwaysDecides = (steps: RuleStep[]): boolean => openPaths(steps, CLEAN).size === 0;

/** Some path replies and then ends without a decision, so the playbooks below still run. */
export const repliesWithoutDeciding = (steps: RuleStep[]): boolean => openPaths(steps, CLEAN).has('replied');

const hasAction = (steps: RuleStep[], matches: (action: string) => boolean): boolean =>
	steps.some((step) => (step.kind === 'action' ? matches(step.action) : step.kind === 'if' && (hasAction(step.then, matches) || hasAction(step.else, matches))));

const hasReply = (steps: RuleStep[]): boolean => hasAction(steps, (action) => REPLIES.has(action));
const sendsToAgent = (steps: RuleStep[]): boolean => hasAction(steps, (action) => action === 'social_agent');

/** Who else still handles the message once the playbooks are done. */
const AFTER: Partial<Record<RuleTrigger, string>> = {
	message_received: 'the playbooks below and Advanced AI still run',
	social_comment: 'the playbooks below and the Social Media Agent still run'
};

/** While a live playbook sends comments to the agent, a comment none decides on is left alone (the backend's route). */
const AFTER_WHEN_AGENT_IS_A_STEP = 'the playbooks below still run; if none decides, the comment is left alone';

export interface ConflictHint {
	text: string;
	/** The playbook that causes it. */
	causedBy: string;
}

const runOrder = (a: RuleRecord, b: RuleRecord): number =>
	a.priority - b.priority ||
	(a.createdAt ?? '￿').localeCompare(b.createdAt ?? '￿');

/** One hint per playbook that needs one, keyed by playbook id. Paused and draft playbooks don't run, so they get none. */
export function conflictHints(rules: RuleRecord[]): Map<string, ConflictHint> {
	const hints = new Map<string, ConflictHint>();
	const runnable = rules.filter((rule) => rule.status === 'live' || rule.status === 'shadow').sort(runOrder);
	const agentIsAStep = runnable.some((rule) => rule.status === 'live' && rule.trigger === 'social_comment' && sendsToAgent(rule.steps));
	const after = (trigger: RuleTrigger): string =>
		trigger === 'social_comment' && agentIsAStep ? AFTER_WHEN_AGENT_IS_A_STEP : (AFTER[trigger] ?? 'the playbooks below still run');
	for (const [index, rule] of runnable.entries()) {
		const earlier = runnable.slice(0, index).filter((other) => other.status === 'live' && other.trigger === rule.trigger);
		const blocker = earlier.find((other) => alwaysDecides(other.steps));
		const replier =
			rule.status === 'live' && (hasReply(rule.steps) || sendsToAgent(rule.steps))
				? earlier.find((other) => repliesWithoutDeciding(other.steps))
				: undefined;
		if (blocker) {
			hints.set(rule.id, { text: `Never runs: “${blocker.name}” above always decides first.`, causedBy: blocker.id });
		} else if (replier && !hasReply(rule.steps)) {
			// The agent replies on its own, after the playbooks: the one-reply rule doesn't reach it.
			hints.set(rule.id, { text: `The Social Media Agent may reply too: “${replier.name}” above can reply first.`, causedBy: replier.id });
		} else if (replier) {
			hints.set(rule.id, {
				text: `Its reply may be skipped: “${replier.name}” above can reply first, and the customer gets one reply.`,
				causedBy: replier.id
			});
		} else if (rule.status === 'live' && repliesWithoutDeciding(rule.steps)) {
			hints.set(rule.id, { text: `Replies without deciding, so ${after(rule.trigger)}.`, causedBy: rule.id });
		}
	}
	return hints;
}

/** For the builder: how many other playbooks this one cuts off or out-replies. */
export function affectedBy(hints: Map<string, ConflictHint>, id: string): number {
	let count = 0;
	for (const [other, hint] of hints) if (other !== id && hint.causedBy === id) count += 1;
	return count;
}
